import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { BRAND } from "@/config/links";

const UPDATED = "22 July 2026";

const SECTIONS = [
  {
    h: "1. Acceptance",
    body: (
      <p>
        By accessing or using <a href="https://interactglobally.com.br/" className="underline decoration-terracotta/60 hover:text-terracotta">interactglobally.com.br</a>{" "}
        (the &ldquo;Website&rdquo;) you accept these Terms. If you do not accept them, please do not
        use the Website. These Terms are governed by the laws of the Federative Republic of Brazil.
      </p>
    ),
  },
  {
    h: "2. About us",
    body: (
      <p>
        The Website is operated by {BRAND.company}, an executive communication consultancy founded
        and led by {BRAND.coach}, based in Brazil. Contact:{" "}
        <a href="mailto:christos@interactglobally.com.br" className="underline decoration-terracotta/60 hover:text-terracotta">christos@interactglobally.com.br</a>.
      </p>
    ),
  },
  {
    h: "3. Nature of the content",
    body: (
      <>
        <p>
          The content of the Website — including articles, case studies, diagnostics, guides,
          methodology descriptions and any downloadable materials — is provided for informational
          and educational purposes.
        </p>
        <p className="mt-3">
          Nothing on the Website constitutes professional advice, a service agreement, or a
          guarantee of any specific business outcome. Coaching or training engagements are only
          established through a signed proposal or agreement.
        </p>
      </>
    ),
  },
  {
    h: "4. Intellectual property",
    body: (
      <>
        <p>
          All content on the Website — including copy, sketches, illustrations, methodology
          frameworks (including &ldquo;Holistic Stage Presence&rdquo; and the &ldquo;Layer-by-Layer
          Method&rdquo;), photography, video, brand marks and software — is owned by {BRAND.company}{" "}
          or its licensors and is protected by Brazilian and international copyright, trademark
          and related-rights law.
        </p>
        <p className="mt-3">
          You may view and read the content for personal, non-commercial use. Any other use —
          copying, republishing, adapting, distributing or exploiting commercially — requires our
          prior written permission.
        </p>
      </>
    ),
  },
  {
    h: "5. Bookings & consultations",
    body: (
      <p>
        You may book a discovery call through our booking partner (TidyCal). Bookings are subject
        to availability and may be rescheduled or cancelled by either party. A discovery call
        does not create a coaching or training engagement — a formal engagement is only in place
        when both parties sign a proposal or scope of work.
      </p>
    ),
  },
  {
    h: "6. Third-party services",
    body: (
      <p>
        The Website integrates services provided by third parties — including ActiveCampaign,
        TidyCal, ScoreApp, Google Analytics, PostHog, Vimeo and social platforms. Your use of
        those services is governed by their own terms and privacy policies, in addition to ours.
        We are not responsible for the availability or content of third-party services.
      </p>
    ),
  },
  {
    h: "7. User conduct",
    body: (
      <p>
        You agree not to use the Website in any way that is unlawful, harmful, misleading, or
        that infringes anyone&rsquo;s rights. In particular you agree not to reverse-engineer,
        scrape, overload or attempt to gain unauthorised access to the Website or any connected
        system.
      </p>
    ),
  },
  {
    h: "8. Disclaimers",
    body: (
      <>
        <p>
          The Website and its content are provided &ldquo;as is&rdquo; without warranties of any kind,
          express or implied, to the fullest extent permitted by law. We do not warrant that the
          Website will be uninterrupted, error-free or that it will produce specific business
          outcomes for the reader.
        </p>
        <p className="mt-3">
          Testimonials and case studies describe individual experiences and are not guarantees
          of similar results.
        </p>
      </>
    ),
  },
  {
    h: "9. Limitation of liability",
    body: (
      <p>
        To the fullest extent permitted by law, {BRAND.company} and its founder are not liable
        for any indirect, incidental, special, consequential or punitive damages arising from
        your use of, or inability to use, the Website. Nothing in these Terms limits our
        liability for wilful misconduct or gross negligence, or for any liability that cannot be
        limited under applicable law.
      </p>
    ),
  },
  {
    h: "10. Privacy",
    body: (
      <p>
        Our processing of your personal data is described in our{" "}
        <a href="/privacy" className="underline decoration-terracotta/60 hover:text-terracotta">Privacy Policy</a>,
        which forms part of these Terms and complies with the LGPD (Lei n&ordm; 13.709/2018).
      </p>
    ),
  },
  {
    h: "11. Changes to these Terms",
    body: (
      <p>
        We may update these Terms from time to time. The &ldquo;Last updated&rdquo; date reflects
        the most recent revision. Continued use of the Website after a change constitutes
        acceptance of the updated Terms.
      </p>
    ),
  },
  {
    h: "12. Governing law & jurisdiction",
    body: (
      <p>
        These Terms are governed by the laws of the Federative Republic of Brazil. Any dispute
        arising out of or relating to the Website or these Terms shall be submitted to the
        competent courts of the place of our registered establishment, without prejudice to any
        mandatory consumer-protection provisions that may apply.
      </p>
    ),
  },
];

export default function Terms() {
  return (
    <>
      <PageHero
        testId="terms-hero"
        eyebrow="Terms of use"
        headline={["The ground rules", "for this website."]}
        emphasis="Straightforward, no legalese theatre."
        emphasisAccent="underline"
        intro={`Last updated ${UPDATED}. These Terms explain the basic rules for using interactglobally.com.br — what the content is, what it is not, and how we work together respectfully.`}
      />

      <section className="relative z-10 pb-32">
        <div className="max-w-[820px] mx-auto px-5 md:px-10 space-y-10 font-body text-ink-soft leading-relaxed">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.h} delay={i * 0.03}>
              <div className="border-t border-ink/15 pt-8">
                <h2 className="font-serif text-2xl md:text-3xl text-ink mb-4">{s.h}</h2>
                <div className="space-y-3">{s.body}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
